import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DATE_FORMAT } from 'constants/date';
import { HIERARCHY_MAX_DEPTH } from 'constants/hierarchy';
import { MAP_LAYERS_NAMES } from 'constants/mapTypes';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useCleanup } from 'hooks/useComponentWillUnmount';
import { IImageryRequest } from 'interfaces';
import MultiRangeSlider, { ChangeResult } from 'multi-range-slider-react';
import { settingsActions } from 'store/slices/mapV2/mapReducer/settingsSlice';
import { typesSelector } from 'store/slices/mapV2/mapReducer/settingsSlice/selectors';
import { imageryActions } from 'store/slices/mapV2/tabsReducer/imagerySlice';
import { getImageryThunk } from 'store/slices/mapV2/tabsReducer/imagerySlice/actions';
import { getAllMapEntitiesThunk } from 'store/slices/mapV2/tabsReducer/layersReducer/mapEntitiesSlice/actions';
import { stateActions } from 'store/slices/mapV2/tabsReducer/layersReducer/stateSlice';
import { initialTimelineTypesState } from 'store/slices/mapV2/tabsReducer/layersReducer/stateSlice/constants';
import {
  filtersSelector,
  timelineTypesSelector,
} from 'store/slices/mapV2/tabsReducer/layersReducer/stateSlice/selectors';
import { TimelineTypes } from 'store/slices/mapV2/tabsReducer/layersReducer/stateSlice/types';
import { operationalActions } from 'store/slices/mapV2/tabsReducer/operationalSlice';
import { TDateRange } from 'types';

import { formatDate } from 'utils';
import {
  useLayerQuickFilterLocalStorage,
  useOperationalLocalStorage,
} from 'utils/localStorage';

import { TimelineSettings } from './TimelineSettings';

import './style.scss';

const FIRST_LABEL_LEFT_OFFSET = 8;

interface TimelineProps {
  histogram: Record<string, number>;
}

interface LocalStorageDateType {
  startDate: Date;
  endDate: Date;
}

const MILLISECONDS_IN_A_DAY = 1000 * 60 * 60 * 24;
const INITIAL_MIN_DATE_OFFSET = 3;

const labels = ['24.02.2022', '2023', '2024', '2025'];

export const Timeline: FC<TimelineProps> = ({ histogram }) => {
  const [maxDays, setMaxDays] = useState(7);
  const [minCaption, setMinCaption] = useState('');
  const [maxCaption, setMaxCaption] = useState('');
  const [scale, setCurrentScale] = useState('7days');
  const [markerTypes, setCurrentMarkerTypes] = useState(['objects']);
  const [dateFilter, setDateFilter] = useState<TDateRange>([null, null]);

  const [quickFilterSettings, setQuickFilterSettings] =
    useLayerQuickFilterLocalStorage();
  const [operationalSettings, setOperationalSettings] =
    useOperationalLocalStorage();

  const timelineTypesState = useAppSelector(timelineTypesSelector);
  const objectFilters = useAppSelector(filtersSelector);
  const mapTypes = useAppSelector(typesSelector);

  const initialSettingsRef = useRef({
    quickFilterSettings,
    operationalSettings,
    objectFilters,
    mapTypes,
  });

  const constructImagerySearchPayload = ({
    startDate,
    endDate,
  }: LocalStorageDateType) =>
    ({
      params: {
        fdate: dayjs(startDate).format(DATE_FORMAT),
        tdate: dayjs(endDate).format(DATE_FORMAT),
        start: 0,
        size: 999999,
      },
      forceStartZero: true,
      isTimeline: true,
    } as IImageryRequest);

  const dispatch = useAppDispatch();

  const getStartDate = (maxDays: number) => {
    const currDateNoTime = new Date();
    currDateNoTime.setHours(0, 0, 0, 0);
    return new Date(currDateNoTime.getTime() - maxDays * MILLISECONDS_IN_A_DAY);
  };

  const startDate = useMemo(() => getStartDate(maxDays), [maxDays]);

  const getDateDifferenceInDays = (laterDate: Date, earlierDate: Date) => {
    const differenceInMs = laterDate.getTime() - earlierDate.getTime();
    const differenceInDays = Math.floor(differenceInMs / MILLISECONDS_IN_A_DAY);
    return differenceInDays;
  };

  const handleFilterEntities = (minDate: Date, maxDate: Date) => {
    dispatch(
      getAllMapEntitiesThunk({
        createdAtFilter: {
          gte: minDate,
          lte: maxDate,
        },
        maxDepth: HIERARCHY_MAX_DEPTH,
        processEntityFn: (entity) => ({
          ...entity,
          state: { ...entity.state, active: true },
        }),
      })
    );
  };

  const getDatesFromChangeResult = (e: ChangeResult) => {
    const startTime = startDate.getTime();

    const minTime = startTime + e.minValue * MILLISECONDS_IN_A_DAY;
    const maxTime = startTime + e.maxValue * MILLISECONDS_IN_A_DAY;
    const minDate = new Date(minTime);
    const maxDate = new Date(new Date(maxTime).setHours(23, 59, 59, 0));

    return [minDate, maxDate];
  };

  const handleDateChange = useCallback(
    (e: ChangeResult) => {
      const [minDate, maxDate] = getDatesFromChangeResult(e);

      setMinCaption(formatDate(minDate));
      setMaxCaption(formatDate(maxDate));
    },
    [startDate]
  );

  const handleEntitiesRequest = useCallback(
    (e: ChangeResult) => {
      const [minDate, maxDate] = getDatesFromChangeResult(e);
      if (
        minDate.getTime() === dateFilter[0]?.getTime() &&
        maxDate.getTime() == dateFilter[1]?.getTime()
      )
        return;

      setDateFilter([minDate, maxDate]);
      const trueMaxDate = e.maxValue === maxDays ? new Date() : maxDate;

      handleSynchronizeRanges([minDate, trueMaxDate], markerTypes);
    },
    [maxDays, markerTypes, dateFilter]
  );

  // Might be needed later
  const drawLabels = () =>
    labels.map((label, idx) => {
      const labelMixin = 'absolute tpg-c2 text-tpg_light w-min top-2';
      const getLeftOffset = (idx: number) => {
        if (idx === 0) return FIRST_LABEL_LEFT_OFFSET;
        const endDate = new Date(2022 + idx, 0, 1);
        const differenceInDays = getDateDifferenceInDays(endDate, startDate);
        return `calc(${(differenceInDays / maxDays) * 100}%)`;
      };
      const leftOffset = getLeftOffset(idx);

      return (
        <div key={idx} className={labelMixin} style={{ left: leftOffset }}>
          {label}
        </div>
      );
    });

  const getDaysFromScale = (scale: string) => Number(scale.split('days')[0]);

  const processObjects = (
    nextTimelineTypesState: Required<TimelineTypes>,
    nextMarkerTypes: string[],
    dates: LocalStorageDateType
  ) => {
    if (nextMarkerTypes.includes('objects')) {
      nextTimelineTypesState = {
        ...nextTimelineTypesState,
        objects: true,
      };
      dispatch(
        stateActions.setFilters({
          ...objectFilters,
          dateFilter: 'other',
          createdAtFilter: {
            gte: dateFilter.at(0),
            lte: dateFilter.at(1),
          },
        })
      );
      handleFilterEntities(dates.startDate, dates.endDate);
      setQuickFilterSettings((current) => ({
        ...current,
        relevance: 'other',
        ...dates,
      }));
    } else if (
      !nextMarkerTypes.includes('objects') &&
      timelineTypesState.objects
    ) {
      nextTimelineTypesState = {
        ...nextTimelineTypesState,
        objects: false,
      };
      dispatch(
        stateActions.setFilters(initialSettingsRef.current.objectFilters)
      );
      setQuickFilterSettings(initialSettingsRef.current.quickFilterSettings);
    } else {
      nextTimelineTypesState = {
        ...nextTimelineTypesState,
        objects: !!timelineTypesState.objects,
      };
    }
    return nextTimelineTypesState;
  };

  const processImagery = (
    nextTimelineTypesState: Required<TimelineTypes>,
    nextMarkerTypes: string[],
    dates: LocalStorageDateType
  ) => {
    if (nextMarkerTypes.includes('imagery')) {
      nextTimelineTypesState = {
        ...nextTimelineTypesState,
        imagery: true,
      };
      dispatch(
        stateActions.setImageryTimelineState({
          ...dates,
          enableFilter: true,
        })
      );
      dispatch(getImageryThunk(constructImagerySearchPayload(dates)));
    } else if (
      !nextMarkerTypes.includes('imagery') &&
      timelineTypesState.imagery
    ) {
      nextTimelineTypesState = {
        ...nextTimelineTypesState,
        imagery: false,
      };
      dispatch(imageryActions.setObjects([]));
      dispatch(stateActions.resetImageryTimelineState());
      dispatch(imageryActions.removeAllVisibleObjects());
    } else {
      nextTimelineTypesState = {
        ...nextTimelineTypesState,
        imagery: !!timelineTypesState.imagery,
      };
    }
    return nextTimelineTypesState;
  };

  const processOperational = (
    nextTimelineTypesState: Required<TimelineTypes>,
    nextMarkerTypes: string[],
    dates: LocalStorageDateType
  ) => {
    if (nextMarkerTypes.includes('operational')) {
      nextTimelineTypesState = {
        ...nextTimelineTypesState,
        operational: true,
      };
      setOperationalSettings((current) => ({
        ...current,
        fire: true,
        gcFrontline: true,
        gcPlacemarks: true,
        relevance: 'other',
        ...dates,
      }));
      dispatch(operationalActions.setRange(dateFilter));
      !mapTypes.fire.active &&
        dispatch(settingsActions.setTypeActive(MAP_LAYERS_NAMES.fire));
      !mapTypes.gcFrontline.active &&
        dispatch(settingsActions.setTypeActive(MAP_LAYERS_NAMES.gcFrontline));
      !mapTypes.gcPlacemarks.active &&
        dispatch(settingsActions.setTypeActive(MAP_LAYERS_NAMES.gcPlacemarks));
    } else if (
      !nextMarkerTypes.includes('operational') &&
      timelineTypesState.operational
    ) {
      nextTimelineTypesState = {
        ...nextTimelineTypesState,
        operational: false,
      };
      setOperationalSettings(initialSettingsRef.current.operationalSettings);
      mapTypes.fire.active !==
        initialSettingsRef.current.mapTypes.fire.active &&
        dispatch(settingsActions.setTypeActive(MAP_LAYERS_NAMES.fire));
      mapTypes.gcFrontline.active !==
        initialSettingsRef.current.mapTypes.gcFrontline.active &&
        dispatch(settingsActions.setTypeActive(MAP_LAYERS_NAMES.gcFrontline));
      mapTypes.gcPlacemarks.active !==
        initialSettingsRef.current.mapTypes.gcPlacemarks.active &&
        dispatch(settingsActions.setTypeActive(MAP_LAYERS_NAMES.gcPlacemarks));
    } else {
      nextTimelineTypesState = {
        ...nextTimelineTypesState,
        operational: !!timelineTypesState.operational,
      };
    }
    return nextTimelineTypesState;
  };

  const handleSynchronizeRanges = (
    dateFilter: TDateRange,
    nextMarkerTypes: string[]
  ) => {
    const dates = {
      startDate: dateFilter.at(0) ?? new Date(),
      endDate: dateFilter.at(1) ?? new Date(),
    };
    let nextTimelineTypesState = initialTimelineTypesState;

    nextTimelineTypesState = processObjects(
      nextTimelineTypesState,
      nextMarkerTypes,
      dates
    );
    nextTimelineTypesState = processImagery(
      nextTimelineTypesState,
      nextMarkerTypes,
      dates
    );
    nextTimelineTypesState = processOperational(
      nextTimelineTypesState,
      nextMarkerTypes,
      dates
    );
    dispatch(stateActions.setTimelineTypes(nextTimelineTypesState));
  };

  const handleChangeCurrentScale = (newScale: string) => {
    setCurrentScale(newScale);
    setMaxDays(getDaysFromScale(newScale));
  };

  const handleChangeCurrentMarkerTypes = (newMarkerType: string) => {
    const nextMarkerTypes = markerTypes.includes(newMarkerType)
      ? markerTypes.filter((v) => v !== newMarkerType)
      : [...markerTypes, newMarkerType];
    setCurrentMarkerTypes(nextMarkerTypes);
    handleSynchronizeRanges(dateFilter, nextMarkerTypes);
  };

  useEffect(() => {
    const currDate = new Date();
    const initialDateFilter: [Date, Date] = [
      getStartDate(INITIAL_MIN_DATE_OFFSET),
      new Date(currDate.setHours(23, 59, 59, 0)),
    ];

    setDateFilter(initialDateFilter);
    handleSynchronizeRanges(initialDateFilter, markerTypes);
  }, []);

  const cleanupFunction = () => {
    handleSynchronizeRanges([null, null], []);
    setTimeout(() => dispatch(stateActions.setTimelineTypes({})), 500);
  };

  useCleanup(cleanupFunction, [handleSynchronizeRanges]);

  return (
    <>
      <MultiRangeSlider
        className="w-full h-8 !rounded-b-none !border-none"
        label={false}
        min={0}
        max={maxDays}
        minValue={maxDays - INITIAL_MIN_DATE_OFFSET}
        maxValue={maxDays}
        step={-1}
        minCaption={minCaption}
        maxCaption={maxCaption}
        onInput={handleDateChange}
        onChange={handleEntitiesRequest}
        ruler={false}
        canMinMaxValueSame
      />
      <TimelineSettings
        currentScale={scale}
        handleChangeCurrentScale={handleChangeCurrentScale}
        currentMarkerTypes={markerTypes}
        handleChangeCurrentMarkerTypes={handleChangeCurrentMarkerTypes}
      />
      {/* hidden until figuring out label logic with scale */}
      {/* {drawLabels()} */}
    </>
  );
};
